<template>
  <div class="flex flex-col w-full h-full bg-n-800 overflow-scroll pt-4 px-4">
    <categories-component
      :catalog-id="catalogId"
      :show-promotions="arePromotionsAvailable"
      :search-query="searchQuery"
      @category-selected="selectCategory"
      @update:search="searchQuery = $event"
      @search-bar-expanded="searchBarExpanded = $event"
    />
    <div v-if="selectedCategoryId === 'promotions' && !searchBarExpanded" class="h-full pt-5">
      <promotion-list
        v-if="availablePromotionsWithNoPoints.length > 0"
        class="pb-8"
        :title="t('ordering.available-promotions')"
        :promotions="availablePromotionsWithNoPoints"
        @promotion-selected="updateTabPromotionIntoTab"
      />
      <promotion-list
        v-if="availablePromotionsWithPoints.length > 0"
        class="pb-8"
        :promotions="availablePromotionsWithPoints"
        :title="t('ordering.available-promotions-with-points')"
        :subtitle="t('ordering.available-points', { points: customerPoints })"
        @promotion-selected="updateTabPromotionIntoTab"
      />
      <promotion-list
        v-if="availablePromotionProducts.length > 0"
        class="pb-8"
        :promotions="availablePromotionProducts"
        :title="t('ordering.promotions-with-points')"
        :subtitle="t('ordering.available-points', { points: customerPoints })"
        @promotion-selected="$emit('selectedProduct', $event)"
      />
    </div>
    <div v-else-if="selectedCategoryId && categories[selectedCategoryId]" class="h-full">
      <div v-if="!searchBarExpanded" class="flex items-center justify-between mt-4">
        <div class="text-n-0 heading-3">
          {{ categories[selectedCategoryId].name }}
        </div>
        <div v-if="!['promotions'].includes(selectedCategoryId)" class="hidden sm:block">
          <l-button color="primary" size="small" icon="plus" @click="newOpenProduct" />
        </div>
      </div>

      <product-list
        class="mt-4"
        :tab-id="tabId"
        :products="productsList"
        @selected-product="(product, quantity) => $emit('selectedProduct', product, quantity)"
      />
      <div v-if="productsList.length === 0" class="h-full flex items-center justify-center">
        <l-empty-case type="empty">
          {{ t('search.no-results') }}

          <template #action>
            <l-button color="primary" size="small" icon="plus" @click="newOpenProduct">
              {{ t('ordering.add-product') }}
            </l-button>
          </template>
        </l-empty-case>
      </div>
    </div>
    <open-product
      v-if="showingOpenProduct"
      :default-name="searchQuery"
      :category-id="selectedCategoryId"
      :course-id="courseId"
      :catalog-id="catalogId"
      @close="showingOpenProduct = false"
      @save="saveOpenProduct"
    />
  </div>
</template>

<script setup lang="ts">
import { useFuse } from '@vueuse/integrations/useFuse'
import { format } from 'date-fns'
import { enUS } from 'date-fns/locale'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { PromotionFilter } from '@last/core'
import { LButton, LEmptyCase } from '@last/core-ui/paprika'

import { useTabs } from '@/composables/useTabs'
import CategoriesComponent from '@/modules/ordering/Categories.vue'
import OpenProduct from '@/modules/ordering/OpenProduct.vue'
import ProductList from '@/modules/ordering/ProductList.vue'
import PromotionList from '@/modules/ordering/PromotionList.vue'
import { managerConfirmation } from '@/plugins/managerConfirmation'
import { useAuthStore } from '@/store/auth'
import { useCatalogStore } from '@/store/catalog'
import { usePromotionsStore } from '@/store/promotions'
import type { CatalogProduct, OpenProduct as OpenProductType, Promotion } from '@/types'

type Props = {
  tabId: string
  customerPoints?: number
  customerUsedPromotions?: string[]
  courseId?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  customerPoints: 0,
  customerUsedPromotions: () => [],
  courseId: null
})

const emit = defineEmits<{
  selectedProduct: [product: CatalogProduct | OpenProductType, quantity?: number]
}>()

const { t } = useI18n()
const promotionsStore = usePromotionsStore()
const catalogStore = useCatalogStore()
const authStore = useAuthStore()

const {
  tabPromotions,
  getCustomerPromotions,
  availablePromotions: allPromotions
} = storeToRefs(promotionsStore)
const { catalogs, categories, products } = storeToRefs(catalogStore)
const { productsByCatalog } = catalogStore
const { locationId } = storeToRefs(authStore)

const route = useRoute()
const { tab, total, catalogId } = useTabs(() => props.tabId)

const selectedCategoryId = ref<string | null>(null)
const showingOpenProduct = ref(false)
const searchQuery = ref('')
const searchBarExpanded = ref<boolean>(false)

const searchProducts = useFuse(searchQuery, productsByCatalog(catalogId.value), {
  fuseOptions: {
    keys: ['name'],
    threshold: 0.4,
    minMatchCharLength: 2
  }
})

const productsList = computed(() => {
  if (searchQuery.value) {
    return searchProducts.results.value.map(r => r.item)
  } else {
    if (!selectedCategoryId.value) return []
    const productIds = categories.value[selectedCategoryId.value]?.products || []
    return productIds.map(id => products.value[id])
  }
})

const catalogCategories = computed(() => {
  if (!catalogId.value) return []
  return catalogs.value[catalogId.value]?.categories ?? []
})

const catalogProductsMap = computed(() => {
  return catalogCategories.value
    .flatMap(categoryId => categories.value[categoryId].products)
    .reduce(
      (res, productId) => {
        const product = products.value[productId]
        if (product) {
          res[product.organizationProductId || product.organizationComboId!] = product
        }
        return res
      },
      {} as Record<string, CatalogProduct>
    )
})

const isFastMode = computed(() => {
  return route.name === 'fastMode'
})

const arePromotionsAvailable = computed(() => {
  return !isFastMode.value && availablePromotions.value.length > 0
})

const availablePromotionsWithNoPoints = computed(() => {
  return availablePromotions.value
    .filter(promotion => !promotion.pointsExpense)
    .filter(promotion => ['2x1', 'percentage'].includes(promotion.discountType))
    .map(promotion => ({
      ...promotion,
      selected: (tabPromotions.value[props.tabId] || []).some(
        tabPromotion => tabPromotion.promotionId === promotion.id
      )
    }))
})

const availablePromotionsWithPoints = computed(() => {
  return availablePromotions.value
    .filter(promotion => promotion.pointsExpense)
    .filter(promotion => ['2x1', 'percentage'].includes(promotion.discountType))
    .map(promotion => ({
      ...promotion,
      selected: (tabPromotions.value[props.tabId] || []).some(
        tabPromotion => tabPromotion.promotionId === promotion.id
      ),
      disabled: promotion.pointsExpense! > props.customerPoints
    }))
})

const availablePromotionProducts = computed(() => {
  return availablePromotions.value
    .filter(promotion => promotion.discountType === 'products')
    .flatMap(promotion => {
      let promotionProducts = promotion.products || []
      if (promotion.categories) {
        promotionProducts = promotion.categories
          .filter(categoryId => !!categories.value[categoryId])
          .flatMap(categoryId => categories.value[categoryId].products)
      }

      return promotionProducts
        .filter(id => catalogProductsMap.value[id])
        .map(id => ({
          ...catalogProductsMap.value[id],
          price: 0,
          disabled: promotion.pointsExpense! > props.customerPoints,
          isValid: promotion.isValid,
          pointsExpense: promotion.pointsExpense
        }))
    })
})

const availablePromotions = computed(() => {
  const customerId = tab.value.customerId
  const promotions = [
    ...allPromotions.value,
    ...(customerId ? getCustomerPromotions.value(customerId) : [])
  ]
    .filter(promotion => ['2x1', 'percentage', 'products'].includes(promotion.discountType))
    .filter(productInCatalog)

  const today = format(new Date(), 'eeee', { locale: enUS }).toLowerCase()

  return new PromotionFilter(
    total.value!,
    locationId.value,
    today,
    tab.value.pickupType,
    customerId,
    props.customerUsedPromotions
  )
    .addValidation(promotions)
    .filter(p => p.isVisible)
})

async function newOpenProduct() {
  const hasPermission = await managerConfirmation('OPEN_PRODUCTS_CREATOR')
  if (hasPermission) {
    showingOpenProduct.value = true
  }
}

function saveOpenProduct(openProduct: OpenProductType) {
  emit('selectedProduct', openProduct, openProduct.quantity)
  showingOpenProduct.value = false
}

function selectCategory(id: string) {
  selectedCategoryId.value = id ?? null
}

async function updateTabPromotionIntoTab(promotion: Promotion) {
  await promotionsStore.updateTabPromotion({
    promotion: {
      promotionId: promotion.id,
      discountType: promotion.discountType as '2x1' | 'percentage' | 'currency',
      discountAmount: promotion.discountAmount ?? undefined,
      name: promotion.name,
      description: promotion.description,
      pointsExpense: promotion.pointsExpense ?? undefined,
      freeDelivery: promotion.freeDelivery,
      allowRepeat: promotion.allowRepeat
    },
    tabId: props.tabId
  })
}

function productInCatalog(promotion: Promotion) {
  if (promotion.products) {
    return promotion.products.some((id: string) => catalogProductsMap.value[id])
  } else if (promotion.categories) {
    return promotion.categories.some((id: string) => catalogCategories.value.includes(id))
  } else {
    return false
  }
}
</script>
